/**
 * Opens and closes the inline predictive-search dropdown.
 *
 * WHY THIS EXISTS
 *
 * assets/predictive-search.js never hides its results panel by state. It closes results in
 * exactly two places — the Escape key, and the query being emptied — and it does not need
 * more than that in stock Horizon, because there the field lives inside a dialog
 * (snippets/search-modal.liquid): whatever is in the panel is invisible until the dialog is
 * opened, and closing the dialog puts it away again.
 *
 * This theme puts the field inline in the header (snippets/search.liquid) with no dialog
 * around it, so the panel has nothing hiding it. That produced two separate faults:
 *
 *   1. Results stayed on screen after the visitor clicked away and carried on browsing.
 *   2. Worse, the panel appeared with NO interaction at all. connectedCallback does:
 *
 *          if (RecentlyViewed.getProducts().length > 0) {
 *            requestIdleCallback(() => { this.#loadEmptyState(); });
 *          }
 *
 *      so on every page load, for any visitor who has viewed a product before, it injects a
 *      "recently viewed" panel. Inside a dialog that is a harmless preload. Inline it is a
 *      search box that opens by itself.
 *
 * WHY THE DEFAULT IS CLOSED
 *
 * A first version of this marked the wrapper as *dismissed* on outside interaction. That
 * fixed fault 1 and could not fix fault 2: on a fresh page load nothing has been pressed
 * yet, so there was no dismissal to apply and the preloaded panel showed anyway. The state
 * is therefore positive — the panel is closed until the visitor actually engages the field —
 * which makes "never touched it" the default rather than a case to be caught.
 *
 * WHY AN ATTRIBUTE RATHER THAN THE COMPONENT'S OWN RESET
 *
 * The component exposes resetSearch(), but that also clears the query. Clicking away should
 * put the panel away, not throw away what someone typed; refocusing brings both back.
 *
 * THE CLICK-CANCEL HAZARD
 *
 * Closing on focusout alone is unsafe: pressing the mouse on a result moves focus off the
 * input, and if that hides the panel on `focusout` the element is gone before `click` fires,
 * so the result never opens. Two things prevent it — the pointerdown handler only closes
 * when the press is OUTSIDE the wrapper, and focusout bails out while a press that started
 * inside is still in flight.
 */

const WRAP = '.kurashi-search-inline';
const ATTR = 'data-search-open';

/** True between pointerdown and pointerup when the press began inside the search. */
let pressInside = false;

const open = (wrapper) => wrapper.setAttribute(ATTR, '');
const close = (wrapper) => wrapper.removeAttribute(ATTR);

const wrapperOf = (node) => (node instanceof Element ? node.closest(WRAP) : null);

document.addEventListener(
  'pointerdown',
  (event) => {
    pressInside = Boolean(wrapperOf(event.target));
    // Capture phase, and every wrapper rather than the nearest: the header renders the
    // snippet twice (a desktop field and a mobile one), so both can hold a stale panel.
    for (const wrapper of document.querySelectorAll(WRAP)) {
      if (!wrapper.contains(event.target)) close(wrapper);
    }
  },
  { capture: true, passive: true }
);

document.addEventListener('pointerup', () => { pressInside = false; }, { capture: true, passive: true });

// Focusing or typing in the field is what opens the panel — including revealing results
// already fetched for a query still sitting in the input.
for (const type of ['focusin', 'input']) {
  document.addEventListener(type, (event) => {
    const wrapper = wrapperOf(event.target);
    if (wrapper) open(wrapper);
  });
}

// Keyboard path: tabbing out of the field entirely should not leave a panel floating over
// the page. relatedTarget is where focus is heading — null means it left the document.
document.addEventListener('focusout', (event) => {
  if (pressInside) return;
  const wrapper = wrapperOf(event.target);
  if (!wrapper) return;
  if (!event.relatedTarget || !wrapper.contains(event.relatedTarget)) close(wrapper);
});
